import type { AuditEntry, AuditQuery } from '@obras/shared';
import { auditActionLabel, formatDateTime } from '@obras/shared';
import { ChevronDown, Download, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { PageHeader } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { SearchInput, TextField } from '../components/ui/Field.js';
import {
  Avatar,
  EmptyState,
  ErrorState,
  RefreshingBar,
  Skeleton,
} from '../components/ui/Feedback.js';
import { MultiSelect, SingleSelect, type MultiSelectOption } from '../components/ui/MultiSelect.js';
import { useAudit, useAuditOptions } from '../features/auditoria/api.js';
import { cn } from '../lib/cn.js';

const ICON = { strokeWidth: 1.5 } as const;
const POR_PAGINA = 50;

/** Evita disparar uma requisição por tecla digitada. */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/**
 * O domínio da ação: a parte antes do ponto.
 *
 * Serve para agrupar trinta e tantos verbos numa lista navegável, sem inventar
 * um dicionário que envelhece a cada ação nova. O verbo aparece cru porque é
 * ele que identifica o registro — quem audita precisa do nome exato, não de
 * uma tradução que pode escorregar.
 */
const dominioDe = (action: string): string => action.split('.')[0] ?? action;

function baixarCsv(rows: readonly AuditEntry[]): void {
  const cabecalho = [
    'Data e hora',
    'Usuário',
    'Ação',
    // O verbo gravado vai junto: quem cruza a planilha com o banco precisa dele.
    'Verbo',
    'Entidade',
    'Identificador',
    'Resumo',
    'IP',
    'Alterações',
  ];

  const escapar = (valor: string): string => `"${valor.replace(/"/g, '""')}"`;

  const linhas = rows.map((row) =>
    [
      escapar(formatDateTime(row.createdAt)),
      escapar(row.user?.name ?? ''),
      escapar(auditActionLabel(row.action)),
      escapar(row.action),
      escapar(row.entityType ?? ''),
      escapar(row.entityId ?? ''),
      escapar(row.summary),
      escapar(row.ipAddress ?? ''),
      escapar(row.changes.map((c) => `${c.field}: ${c.from} → ${c.to}`).join(' | ')),
    ].join(';'),
  );

  // BOM na frente, senão o Excel em pt-BR abre os acentos errados.
  const conteudo = `\uFEFF${[cabecalho.join(';'), ...linhas].join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/csv;charset=utf-8' }));

  const link = document.createElement('a');
  link.href = url;
  link.download = `auditoria-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/** Antes → depois, um campo por linha. */
function Changes({ entry }: { entry: AuditEntry }) {
  return (
    <div className="flex flex-col gap-3 bg-sunken px-4 py-3.5">
      {entry.changes.length > 0 ? (
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-left text-ink-3 *:border-b-2 *:border-line-2">
              <th className="w-44 pb-1 font-medium">Campo</th>
              <th className="pb-1 font-medium">Antes</th>
              <th className="pb-1 font-medium">Depois</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {entry.changes.map((change) => (
              <tr key={change.field}>
                <td className="py-1.5 pr-3 font-mono text-[11px] break-all">{change.field}</td>
                <td className="py-1.5 pr-3 break-words text-ink-3">{change.from}</td>
                <td className="py-1.5 break-words">{change.to}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {entry.context ? (
        <pre className="overflow-x-auto font-mono text-[11px] leading-relaxed text-ink-3">
          {entry.context}
        </pre>
      ) : null}

      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-ink-3">
        {entry.entityId ? (
          <div className="flex gap-1.5">
            <dt>Identificador:</dt>
            <dd className="font-mono break-all">{entry.entityId}</dd>
          </div>
        ) : null}
        {entry.userAgent ? (
          <div className="flex min-w-0 gap-1.5">
            <dt className="shrink-0">Origem:</dt>
            <dd className="truncate">{entry.userAgent}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

function Row({ entry }: { entry: AuditEntry }) {
  const [aberto, setAberto] = useState(false);
  const temDetalhe = entry.changes.length > 0 || entry.context !== null;

  return (
    <li className="border-b border-line last:border-b-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3">
        <time className="tabular w-32 shrink-0 font-mono text-[11px] text-ink-3">
          {formatDateTime(entry.createdAt)}
        </time>

        <span className="flex w-44 shrink-0 items-center gap-2 self-center">
          {entry.user ? (
            <>
              <Avatar name={entry.user.name} url={entry.user.avatarUrl} size={20} />
              <span className="truncate text-[13px]">{entry.user.name}</span>
            </>
          ) : (
            <span className="text-[13px] text-ink-3">sistema</span>
          )}
        </span>

        <span className="min-w-0 flex-1 text-[13px]">{entry.summary}</span>

        {/*
          O acontecimento em português, com o verbo gravado no `title`: a tela é
          para quem administra o órgão, e `relatorio.created` não diz nada a
          quem não escreveu o sistema. O verbo continua a um passo de distância
          para quem precisar dele.
        */}
        <span
          title={entry.action}
          className="shrink-0 border border-dashed border-current/40 bg-sunken px-1.5 py-px text-[11px] text-ink-3"
        >
          {auditActionLabel(entry.action)}
        </span>

        {entry.ipAddress ? (
          <span className="tabular w-28 shrink-0 text-right font-mono text-[11px] text-ink-3">
            {entry.ipAddress}
          </span>
        ) : (
          <span className="w-28 shrink-0" />
        )}

        {temDetalhe ? (
          <button
            type="button"
            onClick={() => setAberto((valor) => !valor)}
            aria-expanded={aberto}
            aria-label={aberto ? 'Fechar detalhes' : 'Ver detalhes'}
            className="grid size-6 shrink-0 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-sunken hover:text-ink"
          >
            <ChevronDown
              {...ICON}
              size={15}
              className={cn('transition-transform duration-100', aberto && 'rotate-180')}
              aria-hidden
            />
          </button>
        ) : (
          <span className="size-6 shrink-0" />
        )}
      </div>

      {aberto ? <Changes entry={entry} /> : null}
    </li>
  );
}

export function AuditoriaPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [userIds, setUserIds] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [entityType, setEntityType] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounced(search);
  const options = useAuditOptions();

  const filters = useMemo(
    () =>
      ({
        page,
        perPage: POR_PAGINA,
        from: from || undefined,
        to: to || undefined,
        userIds,
        actions,
        entityType: entityType ?? undefined,
        search: debouncedSearch || undefined,
      }) satisfies Partial<AuditQuery>,
    [page, from, to, userIds, actions, entityType, debouncedSearch],
  );

  const { data, isPending, isFetching, isError, error, refetch } = useAudit(filters);

  /*
   * Filtro novo, primeira página. O ajuste é durante a renderização, e não num
   * efeito: no efeito, o React chegava a montar a lista pedindo a página 4 de
   * um resultado que agora tem uma, e só então corrigia.
   */
  const assinaturaDoFiltro = JSON.stringify([
    from,
    to,
    userIds,
    actions,
    entityType,
    debouncedSearch,
  ]);
  const [assinaturaDoFiltroAnterior, setAssinaturaDoFiltroAnterior] = useState(assinaturaDoFiltro);
  if (assinaturaDoFiltroAnterior !== assinaturaDoFiltro) {
    setAssinaturaDoFiltroAnterior(assinaturaDoFiltro);
    setPage(1);
  }

  const peopleOptions: MultiSelectOption[] = (options.data?.people ?? []).map((person) => ({
    value: person.id,
    label: person.name,
  }));

  /* Ordenado por domínio: trinta verbos soltos não se navega. */
  const actionOptions: MultiSelectOption[] = [...(options.data?.actions ?? [])]
    .sort((a, b) => auditActionLabel(a.action).localeCompare(auditActionLabel(b.action), 'pt-BR'))
    .map((option) => ({
      value: option.action,
      label: auditActionLabel(option.action),
      detail: `${dominioDe(option.action)} · ${option.total} ${option.total === 1 ? 'registro' : 'registros'}`,
      // A busca do painel alcança o verbo gravado, para quem procura por ele.
      terms: [dominioDe(option.action), option.action],
    }));

  const entityOptions: MultiSelectOption[] = (options.data?.entityTypes ?? []).map((tipo) => ({
    value: tipo,
    label: tipo,
  }));

  const ativos =
    userIds.length + actions.length + (entityType ? 1 : 0) + (from ? 1 : 0) + (to ? 1 : 0);

  return (
    <>
      <PageHeader
        title="Auditoria"
        description="Quem fez o quê, quando e de onde. A trilha não se altera nem se apaga."
        action={
          data && data.items.length > 0 ? (
            <Button icon={<Download {...ICON} size={16} />} onClick={() => baixarCsv(data.items)}>
              Exportar página
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-end gap-2">
        <div className="w-36">
          <TextField
            label="De"
            type="date"
            mono
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className="w-36">
          <TextField
            label="Até"
            type="date"
            mono
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>

        <MultiSelect
          label="Pessoa"
          options={peopleOptions}
          selected={userIds}
          onChange={setUserIds}
          searchable
        />
        <MultiSelect
          label="Ação"
          options={actionOptions}
          selected={actions}
          onChange={setActions}
          searchable
        />
        <SingleSelect
          label="Entidade"
          placeholder="Entidade"
          options={entityOptions}
          value={entityType}
          onChange={setEntityType}
          className="w-40"
        />

        {ativos > 0 || search ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFrom('');
              setTo('');
              setUserIds([]);
              setActions([]);
              setEntityType(null);
              setSearch('');
            }}
          >
            Limpar
          </Button>
        ) : null}

        <div className="relative ml-auto w-full sm:w-72">
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
            placeholder="Buscar no resumo ou por IP"
            aria-label="Buscar na trilha"
          />
        </div>
      </div>

      <div className="mt-3 mb-4 flex items-center justify-between gap-3">
        <p className="tabular text-[13px] text-ink-3">
          {data ? `${data.total} ${data.total === 1 ? 'registro' : 'registros'}` : ''}
        </p>
        <div className="w-40">
          <RefreshingBar active={isFetching && !isPending} />
        </div>
      </div>

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <Card className="overflow-hidden">
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} className="m-4 h-4 rounded-md" />
          ))}
        </Card>
      ) : data.items.length === 0 ? (
        <Card>
          <EmptyState
            icon={ShieldCheck}
            title="Nada neste recorte"
            description="Remova algum filtro ou amplie o período."
          />
        </Card>
      ) : (
        <>
          <Card className={cn('overflow-hidden', isFetching && 'opacity-60')}>
            <ul>
              {data.items.map((entry) => (
                <Row key={entry.id} entry={entry} />
              ))}
            </ul>
          </Card>

          {data.totalPages > 1 ? (
            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="tabular text-[13px] text-ink-3">
                Página {data.page} de {data.totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={data.page <= 1}
                  onClick={() => setPage((valor) => valor - 1)}
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  disabled={data.page >= data.totalPages}
                  onClick={() => setPage((valor) => valor + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
